import React from "react";
import { ArrowDownRight, ArrowUpRight, Clock, CheckCircle2, XCircle, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function StatusPill({ tx }) {
  const status = tx.status || "approved";
  if (status === "pending") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
            data-testid={`status-pill-${tx.transaction_id}`}>
        <Clock className="h-3 w-3" /> Pending
      </span>
    );
  }
  if (status === "rejected") {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300"
        title={tx.rejection_reason || "Rejected"}
        data-testid={`status-pill-${tx.transaction_id}`}
      >
        <XCircle className="h-3 w-3" /> Rejected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
          data-testid={`status-pill-${tx.transaction_id}`}>
      <CheckCircle2 className="h-3 w-3" /> Approved
    </span>
  );
}

function fmtAmount(t) {
  if (t.original_amount != null && t.original_currency) {
    return (
      <span>
        <span className="tnum">
          {t.original_amount.toLocaleString()} {t.original_currency}
        </span>
        {t.original_currency !== "AED" && t.aed_amount != null && (
          <span
            className="block text-[11px]"
            style={{ color: "var(--text-muted)" }}
          >
            = AED {t.aed_amount.toLocaleString()}
          </span>
        )}
      </span>
    );
  }
  if (t.amount != null) {
    return <span className="tnum">AED {t.amount.toLocaleString()}</span>;
  }
  return "—";
}

function fmtProfit(t) {
  if (t.profit_amount == null) return <span style={{ color: "var(--text-muted)" }}>—</span>;
  return (
    <span data-testid={`tx-profit-${t.transaction_id}`}>
      <span className="tnum">{t.profit_currency} {t.profit_amount.toLocaleString()}</span>
      {t.profit_currency !== "AED" && t.profit_aed != null && (
        <span className="block text-[11px]" style={{ color: "var(--text-muted)" }}>= AED {t.profit_aed.toLocaleString()}</span>
      )}
    </span>
  );
}

export default function TransactionsTable({ transactions, showCustomer = false, showProfit = false, onCancelPending = null, onReverse = null }) {
  if (!transactions?.length) {
    return (
      <div
        data-testid="empty-transactions"
        className="w-card-flat p-10 text-center text-sm"
        style={{ color: "var(--text-muted)" }}
      >
        No transactions yet.
      </div>
    );
  }
  return (
    <div className="w-card-flat overflow-hidden">
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid="transactions-table">
          <thead
            style={{
              background: "var(--bg-soft)",
              color: "var(--text-muted)",
            }}
          >
            <tr>
              <th className="text-left px-4 py-3 label-caps">Date</th>
              <th className="text-left px-4 py-3 label-caps">Type</th>
              <th className="text-left px-4 py-3 label-caps">Status</th>
              {showCustomer && (
                <th className="text-left px-4 py-3 label-caps">Customer</th>
              )}
              <th className="text-right px-4 py-3 label-caps">Amount</th>
              {showProfit && <th className="text-right px-4 py-3 label-caps" data-testid="tx-profit-header">Profit</th>}
              <th className="text-right px-4 py-3 label-caps">Points</th>
              <th className="text-right px-4 py-3 label-caps">Balance</th>
              <th className="text-left px-4 py-3 label-caps">By</th>
              {(onCancelPending || onReverse) && <th className="text-right px-4 py-3 label-caps">Action</th>}
            </tr>
          </thead>
          <tbody>
            {transactions.map((t) => (
              <tr
                key={t.id}
                data-testid={`tx-row-${t.id}`}
                style={{ borderTop: "1px solid var(--border)" }}
              >
                <td className="px-4 py-3 whitespace-nowrap" style={{ color: "var(--text-muted)" }}>
                  {fmtDate(t.timestamp)}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                      t.type === "Earn"
                        ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                        : "bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                    }`}
                  >
                    {t.type === "Earn" ? (
                      <ArrowUpRight className="h-3 w-3" />
                    ) : (
                      <ArrowDownRight className="h-3 w-3" />
                    )}
                    {t.type}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <StatusPill tx={t} />
                  {t.status === "rejected" && t.rejection_reason && (
                    <div className="text-[11px] mt-1 italic" style={{ color: "var(--text-muted)" }}>
                      {t.rejection_reason}
                    </div>
                  )}
                </td>
                {showCustomer && (
                  <td className="px-4 py-3">
                    <div className="font-medium">{t.customer_name}</div>
                    <div className="font-mono text-[11px]" style={{ color: "var(--text-muted)" }}>
                      {t.customer_id}
                    </div>
                  </td>
                )}
                <td className="px-4 py-3 text-right">{fmtAmount(t)}</td>
                {showProfit && <td className="px-4 py-3 text-right">{fmtProfit(t)}</td>}
                <td
                  className={`px-4 py-3 text-right tnum font-semibold ${
                    t.points >= 0
                      ? "text-emerald-700 dark:text-emerald-400"
                      : "text-amber-800 dark:text-amber-300"
                  }`}
                >
                  {t.points >= 0 ? "+" : ""}
                  {t.points.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right tnum">
                  {t.balance_after?.toLocaleString?.() ?? "—"}
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>
                  {t.processed_by}
                </td>
                {onCancelPending && (
                  <td className="px-4 py-3 text-right">
                    {t.status === "pending" ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full h-7 text-xs"
                        onClick={() => onCancelPending(t)}
                        data-testid={`cancel-pending-${t.transaction_id}`}
                      >
                        Cancel
                      </Button>
                    ) : null}
                  </td>
                )}
                {onReverse && !onCancelPending && (
                  <td className="px-4 py-3 text-right">
                    {t.reversed_by ? (
                      <span className="text-[11px] italic" style={{ color: "var(--text-muted)" }}
                            data-testid={`reversed-label-${t.transaction_id}`}>
                        Reversed
                      </span>
                    ) : t.type !== "Reversal" && (t.status || "approved") === "approved" ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full h-7 text-xs"
                        onClick={() => onReverse(t)}
                        data-testid={`reverse-tx-${t.transaction_id}`}
                      >
                        <Undo2 className="h-3 w-3 mr-1" /> Reverse
                      </Button>
                    ) : null}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile stacked cards */}
      <ul className="md:hidden divide-y" style={{ borderColor: "var(--border)" }}>
        {transactions.map((t) => (
          <li key={t.id} className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>
                  {fmtDate(t.timestamp)}
                </div>
                {showCustomer && (
                  <div className="mt-1 font-medium text-sm">
                    {t.customer_name}
                  </div>
                )}
                <div className="mt-1 text-sm">{fmtAmount(t)}</div>
                {showProfit && t.profit_amount != null && (
                  <div className="text-xs" style={{ color: "var(--text-muted)" }}>Profit {fmtProfit(t)}</div>
                )}
              </div>
              <div className="text-right">
                <div
                  className={`num-big ${
                    t.points >= 0
                      ? "text-emerald-700 dark:text-emerald-400"
                      : "text-amber-800 dark:text-amber-300"
                  }`}
                >
                  {t.points >= 0 ? "+" : ""}
                  {t.points}
                </div>
                <div className="label-caps mt-1">
                  bal {t.balance_after}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
